import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, Star, Filter, Search, X, RefreshCcw, ShoppingBag, Ban } from 'lucide-react'; 
import { useCart } from '../context/CartContext'; 
import axios from 'axios';
import SEO from '../components/SEO';


const Category = () => {
  const { type } = useParams();
  const [dbProducts, setDbProducts] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [searchQuery, setSearchQuery] = useState(''); 
  const [addedId, setAddedId] = useState(null); 

  const { addToCart, toggleWishlist, wishlistItems } = useCart(); 

  const isWishlisted = (id) => wishlistItems.some(item => (item._id || item.id) === id);

  useEffect(() => {
    const fetchDBProducts = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`https://wethrift-backend.onrender.com/api/product?category=${type.toLowerCase()}`);
        setDbProducts(res.data);
      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDBProducts();
  }, [type]);

  let displayProducts = [...dbProducts];
  
  // Filters Logic
  if (searchQuery) {
    displayProducts = displayProducts.filter(p => 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (p.brand && p.brand.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }
  if (selectedBrand !== 'All') {
    displayProducts = displayProducts.filter(p => p.brand === selectedBrand);
  }
  if (sortBy === 'low') displayProducts.sort((a, b) => a.price - b.price);
  if (sortBy === 'high') displayProducts.sort((a, b) => b.price - a.price);

  const brands = ['All', ...new Set(dbProducts.map(p => p.brand || "ARCHIVE"))];

  const handleQuickAdd = (e, product) => {
    e.preventDefault(); 
    e.stopPropagation();
    if (product.stock <= 0) return;

    // Admin dwara select ki gayi pehli size default banegi
    const defaultSize = product.sizes && product.sizes.length > 0 ? product.sizes[0] : "FREE SIZE";

    addToCart(product, defaultSize); 
    setAddedId(product._id || product.id);
    setTimeout(() => setAddedId(null), 2000); 
  };

  return (
    <div className="bg-[#fcfcfc] min-h-screen pt-24 pb-20 text-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* HEADER SECTION */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-12 border-b pb-10 border-slate-200">
          <div>
            <nav className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4 flex gap-2">
              <Link to="/" className="hover:text-black transition">Home</Link> / 
              <span className="text-black italic">{type}</span>
            </nav>
            <h1 className="text-5xl lg:text-7xl font-black italic uppercase tracking-tighter leading-none">
              {type} Collection
            </h1>
          </div>
          
          <div className="flex flex-wrap gap-4 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input 
                type="text" 
                placeholder="Search pieces..."
                className="w-full bg-white border border-slate-200 pl-12 pr-4 py-4 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-black outline-none transition-all shadow-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select 
              className="bg-white border border-slate-200 px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest outline-none focus:ring-2 focus:ring-black shadow-sm"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Latest Arrivals</option>
              <option value="low">Price: Low to High</option>
              <option value="high">Price: High to Low</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* SIDEBAR FILTERS */}
          <aside className="w-full lg:w-64 space-y-8 shrink-0">
            <div className="sticky top-32">
              <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-6 flex items-center gap-2">
                <Filter size={14} /> Filter by Brand
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                {brands.map(brand => (
                  <button 
                    key={brand} 
                    onClick={() => setSelectedBrand(brand)} 
                    className={`text-left text-[11px] font-black uppercase px-5 py-4 rounded-xl transition-all border ${selectedBrand === brand ? 'bg-black text-white border-black italic translate-x-2' : 'bg-white text-slate-500 border-slate-100 hover:border-slate-300'}`}
                  >
                    {brand}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* PRODUCT GRID */}
          <main className="flex-1">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-40 gap-4">
                <RefreshCcw className="animate-spin text-slate-200" size={48}/>
                <p className="text-[10px] font-black uppercase tracking-widest animate-pulse">Synchronizing Inventory...</p>
              </div>
            ) : displayProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
                {displayProducts.map((product) => {
                  const pId = product._id || product.id;
                  const isOut = product.stock <= 0;

                  return (

                    <>
             <SEO 
  title={`${type?.toUpperCase() || 'Category'} | Archive`} 
  description={`Discover premium thrifted ${type} assets. Handpicked quality for the archive.`}
  url={`/category/${type}`}
/>
                    <div key={pId} className="group relative flex flex-col">
                      <Link to={`/product/${pId}`} className="relative aspect-[3/4] overflow-hidden rounded-[2rem] bg-slate-100 border border-slate-100">
                        {isOut && (
                           <div className="absolute inset-0 z-20 bg-white/60 backdrop-blur-[2px] flex items-center justify-center">
                              <span className="bg-black text-white px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest italic flex items-center gap-2">
                                <Ban size={12}/> Sold Out
                              </span>
                           </div>
                        )}
                        <img 
                          src={product.img} 
                          className={`w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 ${isOut ? 'grayscale opacity-50' : ''}`}
                          alt={product.name}
                          crossOrigin="anonymous"
                        />
                        
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                          {!isOut && (
                            <button 
                              onClick={(e) => handleQuickAdd(e, product)}
                              className={`py-3 px-6 rounded-2xl font-black text-[10px] uppercase transition-all flex items-center gap-2 ${addedId === pId ? 'bg-emerald-500 text-white' : 'bg-white text-black hover:bg-black hover:text-white'}`}
                            >
                              <ShoppingBag size={14}/> {addedId === pId ? 'In Bag' : 'Add to Bag'}
                            </button>
                          )}
                        </div>

                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(product); }}
                          className="absolute top-6 right-6 p-3 rounded-2xl bg-white shadow-xl z-10 transition hover:scale-110"
                        >
                          <Heart size={16} fill={isWishlisted(pId) ? "#ef4444" : "none"} className={isWishlisted(pId) ? "text-red-500" : "text-black"} />
                        </button>
                      </Link>

                      <div className="pt-6 px-2 space-y-2">
                        <div className="flex justify-between items-start gap-4">
                          <div className="max-w-[70%]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 italic">{product.brand || "ARCHIVE"}</p>
                            <h2 className="text-base font-black text-black italic uppercase tracking-tight leading-tight group-hover:underline">{product.name}</h2>
                          </div>
                          <p className="text-xl font-black italic tracking-tighter">₹{product.price}</p>
                        </div>

                        {/* SIZES FROM ADMIN */}
                        <div className="flex flex-wrap gap-1.5 pt-2">
                          {product.sizes && product.sizes.length > 0 ? (
                            product.sizes.map(s => (
                              <span key={s} className="px-2 py-1 border border-slate-200 rounded-lg text-[9px] font-bold text-slate-500 uppercase">
                                {s}
                              </span>
                            ))
                          ) : (
                            <span className="text-[9px] font-bold text-slate-400 uppercase italic">Free Size</span>
                          )}
                        </div>
                      </div>
                    </div>
                    </>
                  );
                })}
              </div>
            ) : (
              <div className="h-96 flex items-center justify-center border-2 border-dashed border-slate-200 rounded-[3rem]">
                <p className="text-[10px] font-black uppercase tracking-[0.5em] text-slate-300">No Inventory Found</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
    
  );
};

export default Category;